import { DragEvent, FC, ReactNode } from 'react';

/**
 * Calendar Cell Data
 */
export interface CalendarCell {
  day?: number;
  content?: ReactNode;
  isToday?: boolean;
  isSelected?: boolean;
  isDisabled?: boolean;
  className?: string;
  onClick?: () => void;
  onDrop?: (e: DragEvent<HTMLDivElement>) => void;
  onDragOver?: (e: DragEvent<HTMLDivElement>) => void;
  onDragEnter?: (e: DragEvent<HTMLDivElement>) => void;
  onDragLeave?: (e: DragEvent<HTMLDivElement>) => void;
}

/**
 * Calendar Table Props
 */
export interface CalendarTableProps {
  /** Days of the week headers */
  weekDays?: string[];
  /** Weeks data (array of 7 cells per week) */
  weeks: CalendarCell[][];
  /** Optional className for wrapper */
  className?: string;
  /** Empty cell message */
  emptyMessage?: string;
}

/**
 * CalendarTable Component
 * 
 * Specialized table for calendar views with proper grid layout
 * Built with Tailwind CSS for consistent styling
 */
export const CalendarTable: FC<CalendarTableProps> = ({
  weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
  weeks,
  className = '',
  emptyMessage = 'No calendar data',
}) => {
  if (!weeks || weeks.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 text-sm">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className={`overflow-x-auto ${className}`}>
      <div className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
        {/* Header */}
        <div className="grid grid-cols-7 bg-gray-50 border-b-2 border-gray-200">
          {weekDays.map((day) => (
            <div
              key={day}
              className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200 last:border-r-0"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Weeks */}
        <div className="bg-white">
          {weeks.map((week, weekIndex) => (
            <div key={weekIndex} className="grid grid-cols-7 border-b border-gray-200 last:border-b-0">
              {week.map((cell, cellIndex) => {
                const isEmpty = !cell || (!cell.day && !cell.content);
                
                return (
                  <div
                    key={cellIndex}
                    data-calendar-cell
                    onClick={cell?.onClick}
                    onDrop={cell?.onDrop}
                    onDragOver={cell?.onDragOver}
                    onDragEnter={cell?.onDragEnter}
                    onDragLeave={cell?.onDragLeave}
                    className={`
                      p-2 border-r border-gray-200 last:border-r-0
                      ${isEmpty ? 'bg-gray-50 min-h-[100px]' : 'bg-white min-h-[100px]'}
                      ${cell?.isToday ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''}
                      ${cell?.isSelected ? 'bg-blue-100' : ''}
                      ${cell?.isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
                      ${cell?.onClick && !cell?.isDisabled ? 'cursor-pointer hover:bg-gray-50' : ''}
                      ${cell?.onDrop ? 'cursor-move' : ''}
                      transition-colors duration-150
                      ${cell?.className || ''}
                    `}
                  >
                    {!isEmpty && (
                      <>
                        {cell.day && (
                          <div className={`
                            text-sm font-semibold mb-2
                            ${cell.isToday ? 'text-blue-600' : 'text-gray-700'}
                          `}>
                            {cell.day}
                          </div>
                        )}
                        {cell.content}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

